import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CustomTextField } from "../../components/CustomFields/TextField";
import { LoadingButton } from "@mui/lab";
import { EncryptDecryptService } from "../../services/common/EncryptionService";
import SkeletonWrapper from "../../components/Skeleton/SkeletonWrapper";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import TeamMembersDraggableList from "../../components/DraggableTable/TeamMembersDraggableList";
import {
  CustomSelect,
  SimpleAsyncSelect,
} from "../../components/CustomFields/CustomSelect";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import RemoveIcon from "@mui/icons-material/Remove";
import ReactQuill from "react-quill";
import { PreferencesContext } from "../../PreferenceContext";
import {
  CreateProcedure,
  GetProcedureDetailByID,
  UpdateProcedure,
} from "../../services/ProcedureService";
import { GetItemList, GetItemDetail } from "../../services/ItemService";
import { AlertType } from "../../services/models/AlertType";
import { PAGE_SIZE } from "../../services/common/const";
import ItemSave from "../Item/ItemSave";
import TeamMemberSave from "../Team/Staff/TeamMemberSave";
import {
  GetMultipleTeamMemberList,
  GetTeamMemberDetail,
} from "../../services/TeamMemberService";
import ProcedurePrescription from "./Components/Prescription";
import { useQuery } from "../../shared/CommonFunction";
import { GetSpecialityList } from "../../services/SpecialityService";
import { GetDepartmentList } from "../../services/DepartmentService";

// SCHEMA FOR OPTION
interface selectOption {
  label: string;
  value: string;
}

// SCHEMA FOR PARAMETER
interface Parameter {
  departmentId: string;
  branchId: string;
  orgId: string;
  consumerId: string;
  pageIndex: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: string;
  searchValue: string;
}

// DEFAULT PARAMS -- ADD HERE IF YOU NEED NEW PARAMETER
const parameters: Parameter = {
  departmentId: "",
  branchId: "",
  pageIndex: 1,
  pageSize: PAGE_SIZE,
  sortColumn: "",
  sortDirection: "",
  searchValue: "",
  orgId: "",
  consumerId: "",
};

interface formInitialValueTypes {
  procedureId: number;
  procedureName: string;
  description: string;
  price: number;
  durationHoursValue: number;
  durationMinuteValue: number;
  displayInOPD: boolean;
  departmentId: selectOption | null;
  specialityId: selectOption | null;
  procedureVisitColl: Array<IProcedureVisit>;
}

interface IProcedureVisit {
  procedureVisitId?: number;
  procedureId: number;
  procedureName: string;
  price: number;
  durationHoursValue: number;
  durationMinuteValue: number;
  description?: string;
  prevProcedureVisitId?: number;
  gapFromPrevVisitValue: string;
  gapFromPrevVisitCode: string;
  gapFromPrevVisitName: string;
  isDeleted: boolean;
  displayOrder: number;
  mode: string;
  procedureVisitConsumableColl: Array<IProcedureVisitConsumable>;
  procedureVisitTeamMemberColl: Array<IProcedureVisitTeamMember>;
  procedureVisitMedicineColl: Array<IProcedureVisitMedicineData>;
}

interface IProcedureVisitTeamMember {
  procedureVisitTeamMemberId?: number | null;
  teamMemberId: number;
  teamMemberName: string;
  teamMemberRole: string;
  qualificationName: string;
  displayOrder: number;
  parentDisplayOrder: number;
  isAnesthetist: boolean;
  isDeleted: boolean;
  mode: string;
}

interface IProcedureVisitConsumable {
  procedureVisitConsumableId?: number;
  itemId: number;
  itemName: string;
  quantity: number;
  unitPrice: number;
  isDeleted: boolean;
  displayOrder: number;
  parentDisplayOrder: number;
  unitName: string;
  mode: string;
}

// NEW: Medicine data structure
interface IProcedureVisitMedicineData {
  procedureVisitMedicineId?: number;
  isMedicineNoteEnabled: boolean;
  medicineNote: string;
  isInstructionNoteEnabled: boolean;
  instructionNote: string;
  parentDisplayOrder: number;
  isDeleted: boolean;
  procedureVisitMedicineDetailColl: Array<IMedicineDetail>;
  procedureVisitMedicineInstructionColl: Array<IMedicineInstruction>;
  mode: string;
  displayOrder: number;
}

interface IMedicineDetail {
  procedureVisitMedicineDetailId?: number;
  medicineId: number;
  medicineName: string;
  medicineDoseId: number;
  displayOrder: number;
  printUnit?: string;
  routeOfAdministrationId?: number;
  quantityUnit?: string;
  relationToFoodTimeGapId?: number;
  isDeleted: boolean;
  mode: string;
}

interface IMedicineInstruction {
  procedureVisitMedicineInstructionId?: number;
  instructionId: number;
  instructionData: string;
  displayOrder: number;
  isDeleted: boolean;
  mode: string;
}

const SubProcedureVisit = (Props: any) => {
  //Props
  const {
    isForPopUp = false,
    handleClosePopUp,
    isFieldEnabled,
    isMemberProcedure = false,
    memberProcedureData = null,
    procedureNames = null,
  } = Props;

  // API Integration - Add these state variables
  const edService = new EncryptDecryptService();
  const [param, setParam] = useState(parameters);
  const navigate = useNavigate();

  const [originalProcedure, setOriginalProcedure] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showConsumables, setShowConsumables] = useState(false);
  const [showMedicine, setShowMedicine] = useState(false);
  const [showTeamMembers, setShowTeamMembers] = useState(false);
  const { userContext, updateUserContext } = useContext(PreferencesContext);
  const theme = useTheme();
  const mdd = useMediaQuery(theme.breakpoints.down("md"));
  const [consumableColl, setConsumableColl] = useState<any[]>([]);
  const [teamMembersColl, setTeamMembersColl] = useState<any[]>([]);
  const [selectedConsumable, setSelectedConsumable] = useState<string | null>(
    null,
  );
  const [teamAnchorEl, setTeamAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any>(null);
  const [anchorEl3, setAnchorEl3] = useState<HTMLButtonElement | null>(null);
  const [specialityData, setSpecialityData] = useState<any[]>([]);
  const [departmentData, setDepartmentData] = useState<any[]>([]);

  const [customPopUp, setCustomPopUp] = React.useState({
    open: false,
    name: "",
    code: "",
    data: "",
  });

  // Form initialization
  const initProcedureData: formInitialValueTypes = {
    procedureId: 0,
    procedureName: isForPopUp ? procedureNames : "",
    description: "",
    price: 0,
    durationHoursValue: 0,
    durationMinuteValue: 0,
    displayInOPD: false,
    departmentId:
      userContext?.departmentId > 0
        ? {
            label: userContext?.departmentName || "",
            value: userContext?.departmentId?.toString(),
          }
        : null,
    specialityId:
      userContext?.specialityId > 0
        ? {
            label: userContext?.specialityName || "",
            value: userContext?.specialityId?.toString(),
          }
        : null,
    procedureVisitColl: [
      {
        procedureVisitId: 0,
        procedureId: 0,
        procedureName: "",
        price: 0,
        durationHoursValue: 0,
        durationMinuteValue: 0,
        prevProcedureVisitId: 0,
        gapFromPrevVisitValue: "",
        gapFromPrevVisitCode: "",
        gapFromPrevVisitName: "",
        isDeleted: false,
        displayOrder: 0,
        mode: "I",
        procedureVisitConsumableColl: [],
        procedureVisitTeamMemberColl: [],
        procedureVisitMedicineColl: [],
      },
    ],
  };

  //SCHEMA VALIDATION
  const validationSchema = Yup.object().shape({
    procedureId: Yup.number().nullable(),
    procedureName: Yup.string().required(),
    description: Yup.string().nullable(),
    price: Yup.number().required(),
    durationHoursValue: Yup.number().nullable(),
    durationMinuteValue: Yup.number().nullable(),
    displayInOPD: Yup.boolean().required(),
    departmentId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Department is required"),
    specialityId: Yup.object()
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .required("Speciality is required"),
    procedureVisitColl: Yup.array()
      .of(
        Yup.object().shape({
          procedureVisitId: Yup.number().nullable(),
          procedureId: Yup.number().nullable(),
          procedureName: Yup.string().nullable(),
          durationHoursValue: Yup.number().nullable(),
          durationMinuteValue: Yup.number().nullable(),
          prevProcedureVisitId: Yup.number().nullable(),
          gapFromPrevVisitValue: Yup.string().nullable(),
          gapFromPrevVisitCode: Yup.string().nullable(),
          gapFromPrevVisitName: Yup.string().nullable(),
          displayOrder: Yup.number().required(),
          isDeleted: Yup.boolean().required(),
          mode: Yup.string().required(),

          procedureVisitConsumableColl: Yup.array()
            .of(
              Yup.object().shape({
                procedureVisitConsumableId: Yup.number().nullable(),
                itemId: Yup.number().required(),
                itemName: Yup.string().nullable(),
                quantity: Yup.number().required(),
                unitPrice: Yup.number().required(),
                unitName: Yup.string().nullable(),
                isDeleted: Yup.boolean().required(),
                displayOrder: Yup.number().required(),
                parentDisplayOrder: Yup.number().required(),
                mode: Yup.string().required(),
              }),
            )
            .required("Visit is required."),
          procedureVisitTeamMemberColl: Yup.array()
            .of(
              Yup.object().shape({
                procedureVisitTeamMemberId: Yup.number().nullable(),
                teamMemberId: Yup.number().required(),
                teamMemberName: Yup.string().nullable(),
                teamMemberRole: Yup.string().required(),
                qualificationName: Yup.string().nullable(),
                displayOrder: Yup.number().required(),
                parentDisplayOrder: Yup.number().required(),
                isDeleted: Yup.boolean().required(),
                isAnesthetist: Yup.boolean().nullable(),
                mode: Yup.string().nullable(),
              }),
            )
            .required("Visit is required."),
          procedureVisitMedicineColl: Yup.array()
            .of(
              Yup.object().shape({
                procedureVisitMedicineId: Yup.number().nullable(),
                isMedicineNoteEnabled: Yup.boolean().nullable(),
                medicineNote: Yup.string().nullable(),
                isInstructionNoteEnabled: Yup.boolean().nullable(),
                instructionNote: Yup.string().nullable(),
                mode: Yup.string().nullable(),
                parentDisplayOrder: Yup.number().nullable(),
                displayOrder: Yup.number().nullable(),
                procedureVisitMedicineDetailColl: Yup.array().of(
                  Yup.object().shape({
                    procedureVisitMedicineDetailId: Yup.number().nullable(),
                    medicineId: Yup.number().nullable(),
                    medicineDoseId: Yup.number().nullable(),
                    displayOrder: Yup.number().nullable(),
                    printUnit: Yup.string().nullable(),
                    routeOfAdministrationId: Yup.number().nullable(),
                    quantityUnit: Yup.string().nullable(),
                    relationToFoodTimeGapId: Yup.number().nullable(),
                    isDeleted: Yup.boolean().nullable(),
                    mode: Yup.string().nullable(),
                  }),
                ),

                procedureVisitMedicineInstructionColl: Yup.array().of(
                  Yup.object().shape({
                    procedureVisitMedicineInstructionId:
                      Yup.number().nullable(),
                    instructionId: Yup.number().nullable(),
                    instructionData: Yup.string().nullable(),
                    displayOrder: Yup.number().nullable(),
                    isDeleted: Yup.boolean().nullable(),
                    mode: Yup.string().nullable(),
                  }),
                ),
              }),
            )
            .required("Visit is required."),
        }),
      )
      .required("Visit is required."),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: initProcedureData,
  });

  const procedureVisitColl = methods.watch("procedureVisitColl") || [];

  const consumableRows =
    procedureVisitColl[0]?.procedureVisitConsumableColl || [];

  const teamMemberRows = (
    procedureVisitColl[0]?.procedureVisitTeamMemberColl || []
  ).filter((row: any) => !row.isDeleted);

  const singleVisitMedicineData = useMemo(() => {
    const medicineColl = procedureVisitColl[0]?.procedureVisitMedicineColl;
    return medicineColl && medicineColl.length > 0 ? medicineColl[0] : null;
  }, [procedureVisitColl]);

  const handleClickOpen = (
    value: boolean,
    label: string,
    code: string,
    data: any,
  ) => {
    setCustomPopUp({
      ...customPopUp,
      open: value,
      name: label,
      code: code,
      data: data,
    });
  };

  let urlParams = useQuery();
  let curRawProcedureId = urlParams.get("q");
  let urlProcedureId = 0;

  // Popup/MemberProcedure mode mein URL param nahi hota — redirect skip karo
  if (!isForPopUp && !isMemberProcedure) {
    if (curRawProcedureId !== null && curRawProcedureId !== undefined) {
      let decId = edService.decrypt(curRawProcedureId);
      if (decId !== "" && decId !== null && decId !== undefined) {
        urlProcedureId = +decId;
      }
    } else {
      navigate("/not-found");
    }
  }

  const handleClose = () => {
    setCustomPopUp({ ...customPopUp, open: false });
  };

  const handleBack = () => {
    if (isForPopUp) {
      handleClosePopUp("CLOSE");
    } else {
      navigate(-1);
    }
  };

  const handleSaveAndTemplate = async () => {
    setIsLoading(true);
    await methods.handleSubmit(async (data: any) => {
      await handleSave(data, true);
    })();
    setIsLoading(false);
  };

  const getNormalizedDurationText = () => {
    const hrs = Number(methods.watch("durationHoursValue") || 0);
    const mins = Number(methods.watch("durationMinuteValue") || 0);

    const totalMinutes = hrs * 60 + mins;
    const normalizedHrs = Math.floor(totalMinutes / 60);
    const normalizedMins = totalMinutes % 60;

    if (normalizedHrs === 0 && normalizedMins === 0) {
      return "";
    }
    if (normalizedHrs === 0) {
      return `${normalizedMins} mins`;
    }
    if (normalizedMins === 0) {
      return `${normalizedHrs} hrs`;
    }
    return `${normalizedHrs} hrs ${normalizedMins} mins`;
  };

  const handleRemoveConsumableSection = () => {
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (visitColl[0]) {
      const detailColl = visitColl[0].procedureVisitConsumableColl || [];
      const updatedColl = detailColl.map((row: any) => ({
        ...row,
        isDeleted: true,
        mode: "D",
      }));

      methods.setValue(
        "procedureVisitColl.0.procedureVisitConsumableColl",
        updatedColl,
        { shouldValidate: true },
      );

      recalcTotalPrice(updatedColl); // all deleted → price = 0
      setShowConsumables(false);
    }
  };

  const handleRemoveMedicineSection = () => {
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (visitColl[0]) {
      const medicineColl = visitColl[0].procedureVisitMedicineColl || [];
      const updatedColl = medicineColl.map((medicine: any) => ({
        ...medicine,
        procedureVisitMedicineDetailColl:
          medicine.procedureVisitMedicineDetailColl?.map((detail: any) => ({
            ...detail,
            isDeleted: true,
            mode: "D",
          })) || [],
        procedureVisitMedicineInstructionColl:
          medicine.procedureVisitMedicineInstructionColl?.map((inst: any) => ({
            ...inst,
            isDeleted: true,
            mode: "D",
          })) || [],
      }));
      methods.setValue(
        "procedureVisitColl.0.procedureVisitMedicineColl",
        updatedColl,
        {
          shouldValidate: true,
        },
      );
      setShowMedicine(false);
    }
  };

  const handleRemoveTeamMemberSection = () => {
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (visitColl[0]) {
      const detailColl = visitColl[0].procedureVisitTeamMemberColl || [];
      const updatedColl = detailColl.map((row: any) => ({
        ...row,
        isDeleted: true,
        mode: "D",
      }));
      methods.setValue(
        "procedureVisitColl.0.procedureVisitTeamMemberColl",
        updatedColl,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      setShowTeamMembers(false);
    }
  };

  const handleManagePopup = async (code: string, data?: any) => {
    setCustomPopUp({ ...customPopUp, open: false });
    if (code === "ITEM_CREATE") {
      const item = await getItemDetail(data?.itemId);
      handleAddConsumableToTable(item);
    } else if (code === "ITEM_UPDATE") {
      const item = await getItemDetail(data?.itemId);
      const visitColl = methods.getValues("procedureVisitColl") || [];
      if (visitColl[0]) {
        const rows = visitColl[0].procedureVisitConsumableColl || [];
        const updatedRows = rows.map((row: any) =>
          row.displayOrder === customPopUp.data.displayOrder
            ? {
                ...row,
                itemId: item.itemId,
                itemName: item.itemName,
                unitPrice: item.unitAmount || 0,
                unitName: item.unitName || "-",
              }
            : row,
        );
        methods.setValue(
          "procedureVisitColl.0.procedureVisitConsumableColl",
          updatedRows,
          { shouldDirty: true, shouldValidate: true },
        );
        recalcTotalPrice(updatedRows);
      }
    } else if (code === "TEAM_MEMBER_CREATE") {
      const teamMember = await getTeamMemberDetailById(data?.teamMemberId);
      handleAddTeamMember(teamMember);
    } else if (code === "TEAM_MEMBER_UPDATE") {
      const value = await getTeamMemberDetailById(data?.teamMemberId);
      const visitColl = methods.getValues("procedureVisitColl") || [];
      if (visitColl[0]) {
        const rows = visitColl[0].procedureVisitTeamMemberColl || [];
        const updatedRows = rows.map((row: any) =>
          row.displayOrder === customPopUp.data.displayOrder
            ? {
                ...row,
                teamMemberId: value.value,
                teamMemberName: value.label,
                teamMemberRole: value.role || 0,
                qualificationName: value.qualification || "",
              }
            : row,
        );
        methods.setValue(
          "procedureVisitColl.0.procedureVisitTeamMemberColl",
          updatedRows,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }
    }
  };

  // Recalculate total price as base price + sum(consumable.quantity * consumable.unitPrice)
  const recalcTotalPrice = (overrideList?: IProcedureVisitConsumable[]) => {
    const visitColl = methods.getValues("procedureVisitColl") || [];
    const list: IProcedureVisitConsumable[] =
      overrideList ?? visitColl[0]?.procedureVisitConsumableColl ?? [];

    const total = list
      .filter((c) => !c.isDeleted)
      .reduce(
        (acc, c) => acc + Number(c.quantity || 0) * Number(c.unitPrice || 0),
        0,
      );

    methods.setValue("price", total, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleConsumableFieldChange = (
    displayOrder: number,
    field: "quantity" | "unitPrice",
    rawValue: string,
  ) => {
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (!visitColl[0]) return;

    const rows: IProcedureVisitConsumable[] =
      visitColl[0].procedureVisitConsumableColl || [];

    const index = rows.findIndex((r) => r.displayOrder === displayOrder);
    if (index === -1) return;

    // Build the updated list immutably
    const updatedRows = rows.map((r, i) =>
      i === index ? { ...r, [field]: Number(rawValue) || 0 } : r,
    );

    methods.setValue(
      "procedureVisitColl.0.procedureVisitConsumableColl",
      updatedRows,
      { shouldDirty: true, shouldValidate: true },
    );

    // Recalc with the fresh list (avoids stale read)
    recalcTotalPrice(updatedRows);
  };

  const fetchItemList = async (searchValue: string, oninit: boolean) => {
    try {
      let params = new URLSearchParams();
      let items = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("productTypeCodes", "HSN,SAC");

      const result: any = await GetItemList(params);
      if (result?.data?.data?.itemColl) {
        items = result?.data?.data?.itemColl;
        if (oninit) setConsumableColl(items);
      }
      return items;
    } catch (error) {
      console.error("Error fetching items:", error);
      return [];
    }
  };

  // FILTER  CONSUMABLES
  async function filterItemList(inputValue: string) {
    let result = await fetchItemList(inputValue, false);
    return result;
  } //  CONSUMABLES LOAD ON SEARCH

  const loadConsumableList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterItemList(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  const handleSave = async (
    value: formInitialValueTypes,
    shouldSaveTemplate = false,
  ) => {
    setIsSaving(true);
    const visits = value?.procedureVisitColl || [];

    const procedureVisitcoll = visits.map((x: any) => ({
      procedureVisitId: x.procedureVisitId || 0,
      procedureId: x.procedureId || 0,
      procedureName: x.procedureName || "",
      description: x.description || "",
      price: x.price || 0,
      prevProcedureVisitId: x.prevProcedureVisitId || 0,
      durationHoursValue: x.durationHoursValue || 0,
      durationMinuteValue: x.durationMinuteValue || 0,
      gapFromPrevVisitValue: x.gapFromPrevVisitValue || "",
      gapFromPrevVisitCode: x.gapFromPrevVisitCode || "",
      gapFromPrevVisitName: x.gapFromPrevVisitName || "",
      displayOrder: x.displayOrder || 0,
      isDeleted: x.isDeleted || false,
      mode: x.mode || "I",
    }));

    const consumableColl = visits.reduce((acc: any[], visit: any) => {
      (visit?.procedureVisitConsumableColl || []).forEach((item: any) => {
        acc.push({
          ...item,
          parentDisplayOrder: visit.displayOrder || 0,
        });
      });
      return acc;
    }, []);

    const teamMemberColl = visits.reduce((acc: any[], visit: any) => {
      (visit?.procedureVisitTeamMemberColl || []).forEach((item: any) => {
        acc.push({
          ...item,
          parentDisplayOrder: visit.displayOrder || 0,
        });
      });
      return acc;
    }, []);

    const medicineColl = visits.reduce((acc: any[], visit: any) => {
      (visit?.procedureVisitMedicineColl || []).forEach((m: any) => {
        acc.push({
          procedureVisitMedicineId: m.procedureVisitMedicineId || 0,
          isMedicineNoteEnabled: m.isMedicineNoteEnabled || false,
          medicineNote: m.medicineNote || null,
          isInstructionNoteEnabled: m.isInstructionNoteEnabled || false,
          instructionNote: m.instructionNote || null,
          parentDisplayOrder: visit.displayOrder || 0,
          displayOrder: m.displayOrder || 0,
          isDeleted: m.isDeleted || false,
          mode: m.procedureVisitMedicineId > 0 ? "U" : "I",
        });
      });
      return acc;
    }, []);

    const medicineDetailColl = visits.reduce((acc: any[], visit: any) => {
      (visit?.procedureVisitMedicineColl || []).forEach((m: any) => {
        (m?.procedureVisitMedicineDetailColl || []).forEach((d: any) => {
          acc.push({
            ...d,
            parentDisplayOrder: m.displayOrder || 0,
          });
        });
      });
      return acc;
    }, []);

    const medicineInstructionColl = visits.reduce((acc: any[], visit: any) => {
      (visit?.procedureVisitMedicineColl || []).forEach((m: any) => {
        (m?.procedureVisitMedicineInstructionColl || []).forEach((i: any) => {
          acc.push({
            ...i,
            parentDisplayOrder: m.displayOrder || 0,
          });
        });
      });
      return acc;
    }, []);

    try {
      let formData = {
        procedureId: value?.procedureId || 0,
        consumerId: userContext.consumerId,
        organizationId: userContext.organizationId,
        branchId: userContext.branchId,
        specialityId: value.specialityId?.value || 0,
        departmentId: value.departmentId?.value || 0,
        typeCode: "SUB_VISIT",
        name: value.procedureName,
        description: value?.description,
        price: Number(value.price) || 0,
        durationHoursValue: Number(value.durationHoursValue) || 0,
        durationMinuteValue: value.durationMinuteValue,
        displayInOPD: false,
        procedureVisitJson: JSON.stringify(procedureVisitcoll),
        procedureVisitConsumableJson: JSON.stringify(consumableColl),
        procedureVisitTeamMemberJson: JSON.stringify(teamMemberColl),
        procedureVisitMedicineJson:
          medicineColl.length > 0 ? JSON.stringify(medicineColl) : null,

        procedureVisitMedicineDetailJson:
          medicineDetailColl.length > 0
            ? JSON.stringify(medicineDetailColl)
            : null,

        procedureVisitMedicineInstructionJson:
          medicineInstructionColl.length > 0
            ? JSON.stringify(medicineInstructionColl)
            : null,

        isDeleted: false,
        dataManagedBy: userContext.loginDetail.userId,
        mode: value?.procedureId > 0 ? "U" : "I",
      };

      if (isMemberProcedure) {
        if (shouldSaveTemplate) {
          const templateResult: any = await CreateProcedure(formData);
          if (!templateResult?.data) {
            updateUserContext({
              ...userContext,
              isAlert: true,
              alertMessage: templateResult?.message || "Template save failed.",
              alertType: AlertType.Error,
            });
            return;
          }
        }

        const returnData = {
          procedureName: value.procedureName,
          description: value.description,
          price: Number(value.price) || 0,
          durationHoursValue: Number(value.durationHoursValue) || 0,
          durationMinuteValue: Number(value.durationMinuteValue) || 0,
          displayInOPD: false,
          procedureVisitTypeCode: "SUB_VISIT",
          parentDisplayOrder:
            memberProcedureData?.parentDisplayOrder ?? undefined,
          // Pass visit details (consumables, team members, medicines)
          addToExistingMultiple:
            memberProcedureData?.addToExistingMultiple ?? false,
          procedureVisitColl: procedureVisitcoll,
          procedureVisitConsumableColl: consumableColl,
          procedureVisitTeamMemberColl: teamMemberColl,
          procedureVisitMedicineJson:
            medicineColl.length > 0 ? medicineColl : null,
          procedureVisitMedicineDetailColl:
            medicineDetailColl.length > 0 ? medicineDetailColl : null,
          procedureVisitMedicineInstructionColl:
            medicineInstructionColl.length > 0 ? medicineInstructionColl : null,
        };
        if (handleClosePopUp) {
          handleClosePopUp("VISIT_CREATE", returnData);
        }
        if (shouldSaveTemplate) {
          updateUserContext({
            ...userContext,
            isAlert: true,
            alertMessage: "Procedure added and template saved successfully.",
            alertType: AlertType.Success,
          });
        }
        setIsSaving(false);
        return;
      }

      let result: any = "";
      if (urlProcedureId > 0) {
        result = await UpdateProcedure(formData);
      } else {
        result = await CreateProcedure(formData);
      }
      if (result?.data) {
        navigate(-1);
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: result?.message,
          alertType: AlertType.Success,
        });
      } else {
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: "Something went wrong.",
          alertType: AlertType.Error,
        });
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: any,
  ) => {
    setAnchorEl3(event.currentTarget);
    setSelectedConsumable(item);
  };

  const handleCloseMore = () => {
    setAnchorEl3(null);
    setSelectedConsumable(null);
  };

  const handleOpenTeamMore = (
    event: React.MouseEvent<HTMLButtonElement>,
    Item: any,
  ) => {
    setTeamAnchorEl(event.currentTarget);
    setSelectedTeamMember(Item);
  };

  const handleCloseTeamMore = () => {
    setTeamAnchorEl(null);
    setSelectedTeamMember(null);
  };

  const handleMedicineDataChange = (
    medicineData: IProcedureVisitMedicineData,
  ) => {
    methods.setValue(
      `procedureVisitColl.0.procedureVisitMedicineColl`,
      [medicineData],
      {
        shouldDirty: true,
        shouldValidate: true,
      },
    );
  };

  const handleAddConsumableToTable = (selectedItem: any) => {
    if (!selectedItem) return;
    const visitColl = methods.getValues("procedureVisitColl") || [];
    const detailColl: IProcedureVisitConsumable[] =
      visitColl[0]?.procedureVisitConsumableColl || [];

    const maxOrder = detailColl.reduce(
      (acc, curr) => (curr.displayOrder > acc ? curr.displayOrder : acc),
      -1,
    );

    const newRow: IProcedureVisitConsumable = {
      displayOrder: maxOrder + 1,
      procedureVisitConsumableId: 0,
      itemId: selectedItem.itemId,
      itemName: selectedItem.itemName,
      quantity: 1,
      unitPrice: selectedItem.unitAmount || 0,
      unitName: selectedItem.unitName || "-",
      mode: "I",
      isDeleted: false,
      parentDisplayOrder: 0,
    };

    const updatedRows = [...detailColl, newRow];

    methods.setValue(
      "procedureVisitColl.0.procedureVisitConsumableColl",
      updatedRows,
      { shouldValidate: true },
    );

    recalcTotalPrice(updatedRows);
  };

  const handleDeleteConsumable = () => {
    if (!selectedConsumable) return;
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (visitColl[0]) {
      const rows: IProcedureVisitConsumable[] =
        visitColl[0].procedureVisitConsumableColl || [];

      const updatedRows = rows.map((row) =>
        row.displayOrder === selectedConsumable.displayOrder
          ? { ...row, isDeleted: true, mode: "D" }
          : row,
      );

      methods.setValue(
        "procedureVisitColl.0.procedureVisitConsumableColl",
        updatedRows,
        { shouldDirty: true, shouldValidate: true },
      );

      recalcTotalPrice(updatedRows);
    }
    handleCloseMore();
  };

  const handleEditConsumable = () => {
    if (!selectedConsumable) return;
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (visitColl[0]) {
      const rows = visitColl[0].procedureVisitConsumableColl || [];
      const consumableToEdit = rows.find(
        (row) => row.displayOrder === selectedConsumable.displayOrder,
      );
      if (consumableToEdit) {
        handleClickOpen(true, "", "UPDATE_ITEM", consumableToEdit);
      }
    }
    handleCloseMore();
  };

  const handleEditTeamMember = () => {
    if (!selectedTeamMember) return;
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (visitColl[0]) {
      const rows = visitColl[0].procedureVisitTeamMemberColl || [];
      const teamMemberToEdit = rows.find(
        (row) => row.displayOrder === selectedTeamMember.displayOrder,
      );
      if (teamMemberToEdit) {
        handleClickOpen(true, "", "UPDATE_TEAMMEMBER", teamMemberToEdit);
      }
    }
    handleCloseTeamMore();
  };

  const handleDeleteTeamMember = () => {
    if (!selectedTeamMember) return;
    const visitColl = methods.getValues("procedureVisitColl") || [];
    if (visitColl[0]) {
      const rows = visitColl[0].procedureVisitTeamMemberColl || [];
      const updatedRows = rows.map((row: any) =>
        row.displayOrder === selectedTeamMember.displayOrder
          ? { ...row, isDeleted: true, mode: "D" }
          : row,
      );
      methods.setValue(
        "procedureVisitColl.0.procedureVisitTeamMemberColl",
        updatedRows,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
    handleCloseTeamMore();
  };

  const getItemDetail = async (itemId: number) => {
    let item: any;
    let params = new URLSearchParams();
    params.append("itemId", itemId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());

    try {
      const res: any = await GetItemDetail(params);
      item = res.data.data;

      return item;
    } catch (e) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  // Fetch team members from API
  const fetchTeamMemberList = async (searchValue: string, oninit: boolean) => {
    try {
      let params = new URLSearchParams();
      let members = [];
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", searchValue);
      params.append("teamMemberType", "");

      const result: any = await GetMultipleTeamMemberList(params);
      if (result?.data?.data?.teamMemberColl) {
        members = result?.data?.data?.teamMemberColl;
        if (oninit) setTeamMembersColl(members);
      }
      return members;
    } catch (error) {
      console.error("Error fetching team members:", error);
      return [];
    }
  };

  // FILTER  DURATION UNIT
  async function filterTeamMembers(inputValue: string) {
    let result = await fetchTeamMemberList(inputValue, false);
    return result;
  } //  DURATION UNIT LOAD ON SEARCH

  const loadTeamMembersList = (
    inputValue: string,
    callback: (options: any) => void,
  ) => {
    if (inputValue && inputValue.length > 0) {
      filterTeamMembers(inputValue?.trim()).then((r) => {
        callback(r);
      });
    } else {
      callback([]);
    }
  };

  const handleAddTeamMember = (selected: any) => {
    if (!selected) return;
    const visitColl = methods.getValues("procedureVisitColl") || [];
    const detailColl = visitColl[0]?.procedureVisitTeamMemberColl || [];
    const maxRowNumber = detailColl.reduce(
      (acc: number, curr: any) =>
        curr.displayOrder > acc ? curr.displayOrder : acc,
      0,
    );
    const newRow: IProcedureVisitTeamMember = {
      displayOrder: detailColl.length === 0 ? 0 : maxRowNumber + 1,
      procedureVisitTeamMemberId: 0,
      teamMemberId: selected?.value,
      isAnesthetist: selected?.isAnesthesia || false,
      isDeleted: false,
      mode: "I",
      teamMemberName: selected.label,
      teamMemberRole: selected.role,
      parentDisplayOrder: 0,
      qualificationName: selected.qualification || "",
    };
    methods.setValue(
      "procedureVisitColl.0.procedureVisitTeamMemberColl",
      [...detailColl, newRow],
      {
        shouldValidate: true,
      },
    );
  };

  const handleTeamCreate = (val: string) => {
    handleClickOpen(true, val, "CREATE_TEAMMEMBER", "");
  };

  const getProcedureDetailById = async (procedureId: number) => {
    try {
      let procedureData: any;
      let params = new URLSearchParams();
      params.append("procedureId", procedureId?.toString());
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("organizationId", userContext.organizationId?.toString());
      params.append("departmentId", userContext.departmentId?.toString());
      params.append("specialityId", userContext.specialityId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      const result: any = await GetProcedureDetailByID(params);
      if (result?.data?.data) {
        procedureData = result.data.data;

        let detailColl = procedureData?.procedureVisitColl?.map(
          (x: any, xIndex: number) => {
            let procedureVisitDetail: IProcedureVisit = {
              procedureVisitId: x.procedureVisitId || 0,
              displayOrder: x.displayOrder,
              procedureId: x.procedureId,
              procedureName: x.procedureName,
              durationHoursValue: x.durationHoursValue,
              durationMinuteValue: x.durationMinuteValue,
              gapFromPrevVisitValue: "",
              gapFromPrevVisitCode: "",
              gapFromPrevVisitName: "",
              price: x.price,
              isDeleted: false,
              mode: "U",
              procedureVisitConsumableColl: x.procedureVisitConsumableColl
                ?.filter(
                  (mf: any) => x.procedureVisitId === mf.procedureVisitId,
                )
                ?.map((i: any, IIndex: number) => {
                  let Consumable: IProcedureVisitConsumable = {
                    parentDisplayOrder: xIndex,
                    displayOrder: IIndex,
                    procedureVisitConsumableId:
                      i.procedureVisitConsumableId || 0,
                    itemId: i.itemId,
                    itemName: i.itemName || "",
                    quantity: i.quantityUsed || 1,
                    unitPrice: i.unitPrice || 0,
                    unitName: i.unitName || "-",
                    isDeleted: false,
                    mode: "U",
                  };
                  return Consumable;
                }),
              procedureVisitTeamMemberColl: x.procedureVisitTeamMemberColl
                ?.filter(
                  (mf: any) => x.procedureVisitId === mf.procedureVisitId,
                )
                ?.map((i: any, IIndex: number) => {
                  let TeamMember: IProcedureVisitTeamMember = {
                    parentDisplayOrder: xIndex,
                    displayOrder: IIndex,
                    procedureVisitTeamMemberId:
                      i.procedureVisitTeamMemberId || 0,
                    teamMemberId: i.teamMemberId,
                    teamMemberName: i.fullName || "",
                    teamMemberRole: i.teamMemberCode || "",
                    isAnesthetist: i.isAnesthetist || false,
                    isDeleted: false,
                    mode: "U",
                    qualificationName: i.qualification || "",
                  };
                  return TeamMember;
                }),
              procedureVisitMedicineColl: x.procedureVisitMedicine
                ? [
                    {
                      parentDisplayOrder: xIndex,
                      displayOrder: 0,
                      procedureVisitMedicineId:
                        x.procedureVisitMedicine.procedureVisitMedicineId || 0,
                      isMedicineNoteEnabled:
                        x.procedureVisitMedicine.isMedicineNoteEnabled || false,
                      medicineNote: x.procedureVisitMedicine.medicineNote || "",
                      isInstructionNoteEnabled:
                        x.procedureVisitMedicine.isInstructionNoteEnabled ||
                        false,
                      instructionNote:
                        x.procedureVisitMedicine.instructionNote || "",
                      isDeleted: false,
                      mode: "U",

                      procedureVisitMedicineDetailColl:
                        x.procedureVisitMedicine.procedureVisitMedicineDetailColl?.map(
                          (i: any, IIndex: number) => ({
                            displayOrder: IIndex,
                            procedureVisitMedicineDetailId:
                              i.procedureVisitMedicineDetailId || 0,
                            medicineId: i.medicineId,
                            medicineName:
                              i.medicineName || i.medicineLabel || "",
                            medicineDoseId: i.medicineDoseId,
                            printUnit: i.printUnit || i.printUnitName || "",
                            routeOfAdministrationId:
                              i.routeOfAdministrationId || null,
                            quantityUnit: i.quantityUnit || "",
                            relationToFoodTimeGapId:
                              i.relationToFoodTimeGapId || null,
                            isDeleted: false,
                            mode:
                              i.procedureVisitMedicineDetailId > 0 ? "U" : "I",
                          }),
                        ),

                      procedureVisitMedicineInstructionColl:
                        x.procedureVisitMedicine.procedureVisitMedicineInstructionColl?.map(
                          (i: any, IIndex: number) => ({
                            displayOrder: IIndex,
                            procedureVisitMedicineInstructionId:
                              i.procedureVisitMedicineInstructionId || 0,
                            instructionId: i.instructionId,
                            instructionData: i.instructionData || "",
                            isDeleted: false,
                            mode:
                              i.procedureVisitMedicineInstructionId > 0
                                ? "U"
                                : "I",
                          }),
                        ),
                    },
                  ]
                : [],
            };
            return procedureVisitDetail;
          },
        );
        methods.reset(
          {
            ...initProcedureData,
            procedureId: procedureData.procedureId || 0,
            procedureName: procedureData.name || "",
            description: procedureData.description || "",
            price: procedureData.price || 0,
            displayInOPD: procedureData.displayInOPD,
            departmentId: procedureData?.departmentId
              ? {
                  label: procedureData?.departmentName,
                  value: procedureData?.departmentId,
                }
              : null,
            specialityId: procedureData?.specialityId
              ? {
                  label: procedureData?.specialityName,
                  value: procedureData?.specialityId,
                }
              : null,
            durationHoursValue: procedureData.durationHoursValue || 0,
            durationMinuteValue: procedureData.durationMinuteValue || 0,
            procedureVisitColl: detailColl,
          },
          {
            keepErrors: true,
            keepIsValid: true,
            keepDirty: false,
          },
        );
        setShowConsumables(true);
        setShowTeamMembers(true);
        setShowMedicine(true);
        const allConsumables =
          detailColl?.[0]?.procedureVisitConsumableColl ?? [];
        recalcTotalPrice(allConsumables);
      }
      return procedureData;
    } catch (error) {
      console.error("Error fetching single visit procedures:", error);
      return [];
    }
  };

  const getTeamMemberDetailById = async (TeamMemberId: number) => {
    let teamMember: any;
    let params = new URLSearchParams();
    params.append("teamMemberId", TeamMemberId?.toString());
    params.append("consumerId", userContext?.consumerId?.toString());
    params.append("orgId", userContext?.organizationId?.toString());
    params.append("branchId", userContext?.branchId?.toString());
    try {
      const res: any = await GetTeamMemberDetail(params);
      teamMember = res.data.data;
      return teamMember;
    } catch (e: any) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  //DEPARTMENT LIST
  const getDepartmentList = async () => {
    try {
      let params = new URLSearchParams();
      params.append("ConsumerId", userContext?.consumerId.toString());
      params.append("OrgId", userContext?.organizationId.toString());
      params.append("BranchId", userContext?.branchId.toString());
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      const result: any = await GetDepartmentList(params);
      if (result?.data?.data?.departmentColl) {
        const resultData = result?.data?.data.departmentColl;
        setDepartmentData(resultData);
      }
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  };

  //SPECIALITY LIST
  async function fetchSpeciality(departmentIds: string) {
    try {
      let params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId?.toString());
      params.append("orgId", userContext.organizationId?.toString());
      params.append("branchId", userContext.branchId?.toString());
      params.append("departmentIds", departmentIds);
      params.append("pageIndex", param.pageIndex?.toString());
      params.append("pageSize", param.pageSize?.toString());
      params.append("sortColumn", param.sortColumn);
      params.append("sortDirection", param.sortDirection);
      params.append("searchValue", param.searchValue);
      params.append("mode", "DROPDOWN".toString());
      const specialityData: any = await GetSpecialityList(params);
      if (specialityData?.data?.data?.specialityColl) {
        let specialityCollection = specialityData.data.data.specialityColl;
        setSpecialityData(specialityCollection);
      }
    } catch (e: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage:
          typeof e === "string" ? (e as string) : (e?.message as string),
        alertType: AlertType.Error,
      });
    }
  }

  const handleSingleVisitFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Ignore submit events bubbling from nested popup forms.
    if (e.target !== e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    void methods.handleSubmit(onSubmit)(e);
  };

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    await handleSave(data);
    setIsLoading(false);
  };

  const fetchData = async () => {
    try {
      await fetchItemList("", true);
      await fetchTeamMemberList("", true);
      if (urlProcedureId <= 0) {
        await getDepartmentList();
      }
      if (urlProcedureId > 0) {
        await getProcedureDetailById(urlProcedureId);
      } else if (userContext?.departmentId > 0) {
        await fetchSpeciality(userContext?.departmentId?.toString());
        if (userContext?.specialityId > 0) {
          await getProcedureDetailById(urlProcedureId);
        }
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };

  // USEEFFECTS
  useEffect(() => {
    setIsLoading(true);
    fetchData();
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Box
        className={"main-container"}
        sx={{
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FFF4CC",
            color: "#333333",
            padding: "10px 15px",
            marginBottom: "10px",
            border: "1px solid #ebdba3",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography>
            <strong>Sub Visit</strong> is applicable only for the{" "}
            <strong>Multi Visit</strong> setup and is not displayed in{" "}
            <strong>Single Visit OPDs</strong>
          </Typography>
        </Box>

        <FormProvider {...methods}>
          <form onSubmit={handleSingleVisitFormSubmit} action="">
            <Grid container spacing={0}>
              <Grid item xs={12} sx={{ px: 0, pb: 0 }}>
                <Grid
                  container
                  item
                  spacing={1.5}
                  sx={{ position: "relative" }}
                >
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.departmentId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Department *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="departmentId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"departmentId"}
                              FieldName={"departmentId"}
                              options={departmentData}
                              isDisabled={urlProcedureId > 0}
                              openMenuOnFocus
                              isClearable
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                if (
                                  methods
                                    ?.getValues()
                                    ?.departmentId?.value?.toString() !==
                                  e?.value?.toString()
                                ) {
                                  methods.setValue(
                                    "departmentId",
                                    e?.value ? e : null,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                  methods.setValue("specialityId", null, {
                                    shouldValidate: true,
                                  });
                                  if (e?.value) {
                                    setIsLoading(true);
                                    await fetchSpeciality(e.value);
                                    setIsLoading(false);
                                  } else {
                                    setSpecialityData([]);
                                  }
                                }
                              }}
                              className={`text-input ${
                                methods.formState.errors.departmentId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.departmentId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth>
                      <FormLabel
                        className={`fileds-label-text  ${
                          methods.formState.errors.specialityId
                            ? "validation-message"
                            : ""
                        }`}
                      >
                        Speciality *
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          control={methods.control}
                          name="specialityId"
                          render={({ field }) => (
                            <CustomSelect
                              {...field}
                              isNotCreatable={true}
                              id={"specialityId"}
                              FieldName={"specialityId"}
                              options={specialityData}
                              openMenuOnFocus
                              isClearable
                              isDisabled={urlProcedureId > 0}
                              isSearchable
                              menuPortalTarget={document.querySelector("body")}
                              handleChange={async (e: any) => {
                                methods.setValue(
                                  "specialityId",
                                  e?.value ? e : null,
                                  {
                                    shouldValidate: true,
                                  },
                                );
                              }}
                              className={`text-input ${
                                methods.formState.errors.specialityId
                                  ? "error"
                                  : ""
                              }`}
                            />
                          )}
                        />
                      </SkeletonWrapper>
                      <Typography className="validation-message">
                        {methods.formState.errors.specialityId?.message}
                      </Typography>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <FormLabel className="fileds-label-text">
                        Procedure Name
                      </FormLabel>
                      <SkeletonWrapper
                        isLoading={isLoading}
                        className="skeleton-input"
                      >
                        <Controller
                          name="procedureName"
                          control={methods.control}
                          render={({ field }) => (
                            <CustomTextField
                              {...field}
                              fullWidth
                              size="small"
                            />
                          )}
                        />
                      </SkeletonWrapper>
                    </FormControl>
                    <Typography className="validation-message">
                      {methods.formState.errors.procedureName?.message}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} />

                  {isFieldEnabled("DESCRIPTION") && (
                    <Grid item xs={12} sx={{ mb: 0, pb: 0 }}>
                      <FormControl fullWidth sx={{ mb: 0, pb: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                          className="note-container-box"
                        >
                          <FormLabel
                            className="fileds-label-text"
                            sx={{ mb: 0.5 }}
                          >
                            Description
                          </FormLabel>
                        </Box>
                        <Controller
                          control={methods.control}
                          name="description"
                          defaultValue=""
                          render={({ field }) => (
                            <ReactQuill
                              theme="snow"
                              value={field.value || ""}
                              onChange={field.onChange}
                              className="react-quill-alignment"
                            />
                          )}
                        />
                      </FormControl>
                    </Grid>
                  )}

                  {isFieldEnabled("CONSUMABLE") && !showConsumables && (
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Button
                        variant="text"
                        startIcon={<Inventory2OutlinedIcon />}
                        sx={{
                          color: "#285cdb",
                          textTransform: "none",
                        }}
                        onClick={() => setShowConsumables(true)}
                      >
                        CONSUMABLES
                      </Button>
                    </Grid>
                  )}

                  {isFieldEnabled("CONSUMABLE") && showConsumables && (
                    <>
                      <Grid item xs={12} sx={{ display: "flex" }}>
                        <Button
                          variant="text"
                          onClick={handleRemoveConsumableSection}
                          sx={{
                            marginLeft: "auto",
                            color: "red",
                            fontSize: "14px",
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                          Remove Consumable
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          className="template-table-container"
                          sx={{ p: 2 }}
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            border: "1px solid #0000001f",
                            borderRadius: 4,
                          }}
                        >
                          <Box
                            style={{
                              flex: 1,
                              borderRadius: 8,
                              position: "relative",
                              zIndex: 0,
                            }}
                          >
                            {mdd ? (
                              <Box>
                                {consumableRows
                                  .filter((row) => !row.isDeleted)
                                  .map((row) => (
                                    <Box
                                      key={row.displayOrder}
                                      sx={{
                                        border: "1px solid #E9EBEC",
                                        borderRadius: 2,
                                        p: 2,
                                        mb: 2,
                                      }}
                                    >
                                      <Typography fontWeight={600}>
                                        {row.itemName}
                                      </Typography>

                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 1,
                                          mt: 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center",
                                          }}
                                        >
                                          <Typography sx={{ minWidth: 80 }}>
                                            Quantity
                                          </Typography>
                                          <CustomTextField
                                            type="text"
                                            value={row.quantity}
                                            onChange={(e) =>
                                              handleConsumableFieldChange(
                                                row.displayOrder,
                                                "quantity",
                                                e.target.value,
                                              )
                                            }
                                            size="small"
                                            placeholder="0"
                                            sx={{ width: 140 }}
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position="end">
                                                  <Box sx={{ paddingLeft: 1 }}>
                                                    {row.unitName || "-"}
                                                  </Box>
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </Box>

                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            alignItems: "center",
                                          }}
                                        >
                                          <Typography sx={{ minWidth: 80 }}>
                                            Price
                                          </Typography>
                                          <CustomTextField
                                            value={row.unitPrice}
                                            onChange={(e) =>
                                              handleConsumableFieldChange(
                                                row.displayOrder,
                                                "unitPrice",
                                                e.target.value,
                                              )
                                            }
                                            size="small"
                                            placeholder="0"
                                            sx={{ width: 140 }}
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <Box sx={{ paddingRight: 1 }}>
                                                    ₹
                                                  </Box>
                                                </InputAdornment>
                                              ),
                                            }}
                                          />

                                          <Typography sx={{ ml: 2 }}>
                                            Amount: ₹{" "}
                                            {Number(row.quantity || 0) *
                                              Number(row.unitPrice || 0)}
                                          </Typography>
                                          <Box sx={{ ml: "auto" }}>
                                            <IconButton
                                              onClick={(e) =>
                                                handleOpenMore(e, row)
                                              }
                                            >
                                              <MoreVertRoundedIcon />
                                            </IconButton>
                                          </Box>
                                        </Box>
                                      </Box>
                                    </Box>
                                  ))}
                              </Box>
                            ) : (
                              <Table
                                size="small"
                                sx={{
                                  width: "100%",
                                  tableLayout: "fixed",
                                  "& td, & th": {
                                    fontSize: "14px !important",
                                    borderBottom: "1px solid #E9EBEC",
                                    height: "56px",
                                    verticalAlign: "top",
                                    padding: "12px 16px",
                                  },
                                  "& .MuiTableRow-root:hover": {
                                    backgroundColor: "transparent !important",
                                  },
                                }}
                              >
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        width: "25%",
                                        borderTop: "none",
                                        textAlign: "left",
                                        padding: "12px 16px",
                                      }}
                                    >
                                      Consumables/Services
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        width: "25%",
                                        borderTop: "none",
                                        textAlign: "center",
                                        padding: "12px 16px",
                                      }}
                                    >
                                      Quantity
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        width: "20%",
                                        borderTop: "none",
                                        textAlign: "center",
                                        padding: "12px 16px",
                                      }}
                                    >
                                      Price
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        width: "20%",
                                        borderTop: "none",
                                        textAlign: "center",
                                        padding: "12px 16px",
                                      }}
                                    >
                                      Amount
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        width: "10%",
                                        borderTop: "none",
                                        textAlign: "center",
                                        padding: "12px 16px",
                                      }}
                                    />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {consumableRows
                                    .filter((row) => !row.isDeleted)
                                    .map((row) => (
                                      <TableRow key={row.displayOrder}>
                                        <TableCell
                                          sx={{
                                            textAlign: "left",
                                            padding: "12px 16px",
                                          }}
                                        >
                                          {row.itemName}
                                        </TableCell>
                                        {/* Quantity Counter */}
                                        <TableCell
                                          sx={{
                                            textAlign: "center",
                                            padding: "12px 16px",
                                          }}
                                        >
                                          <CustomTextField
                                            type="text"
                                            value={row.quantity}
                                            onChange={(e) =>
                                              handleConsumableFieldChange(
                                                row.displayOrder,
                                                "quantity",
                                                e.target.value,
                                              )
                                            }
                                            size="small"
                                            placeholder="0"
                                            sx={{
                                              width: "150px",
                                              "& .MuiInputBase-root": {
                                                height: "40px",
                                              },
                                            }}
                                            InputProps={{
                                              endAdornment: (
                                                <InputAdornment position="end">
                                                  <Box
                                                    sx={{
                                                      borderLeft:
                                                        "1px solid #c4c4c4",
                                                      paddingLeft: "8px",
                                                      paddingRight: "6px",
                                                      minWidth: "40px",
                                                      textAlign: "center",
                                                      fontSize: "12px",
                                                      color: "#6b7280",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {row.unitName || "-"}
                                                  </Box>
                                                </InputAdornment>
                                              ),
                                            }}
                                          />
                                        </TableCell>
                                        {/* Price Input */}
                                        <TableCell
                                          sx={{
                                            textAlign: "center",
                                            padding: "12px 16px",
                                          }}
                                        >
                                          <CustomTextField
                                            value={row.unitPrice}
                                            onChange={(e) =>
                                              handleConsumableFieldChange(
                                                row.displayOrder,
                                                "unitPrice",
                                                e.target.value,
                                              )
                                            }
                                            size="small"
                                            placeholder="0"
                                            InputProps={{
                                              startAdornment: (
                                                <InputAdornment position="start">
                                                  <Box
                                                    sx={{
                                                      borderRight:
                                                        "1px solid #c4c4c4",
                                                      width: "30px",
                                                      textAlign: "center",
                                                      paddingRight: "8px",
                                                    }}
                                                  >
                                                    ₹
                                                  </Box>
                                                </InputAdornment>
                                              ),
                                            }}
                                            sx={{ width: "120px" }}
                                          />
                                        </TableCell>
                                        <TableCell
                                          align="center"
                                          sx={{
                                            verticalAlign: "middle !important",
                                          }}
                                        >
                                          <Typography>
                                            ₹{" "}
                                            {Number(row.quantity || 0) *
                                              Number(row.unitPrice || 0)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell
                                          sx={{
                                            textAlign: "right",
                                            padding: "12px 16px",
                                          }}
                                        >
                                          <IconButton
                                            onClick={(e) =>
                                              handleOpenMore(e, row)
                                            }
                                          >
                                            <MoreVertRoundedIcon />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            )}

                            <Box
                              sx={{
                                border: "1px solid #ddd",
                                borderTop: "none",
                                borderRadius: 2,
                                backgroundColor: "#fff",
                                mt: 2,
                              }}
                            >
                              <SkeletonWrapper
                                isLoading={isLoading}
                                className="skeleton-input"
                              >
                                <SimpleAsyncSelect
                                  id="consumable"
                                  FieldName="consumable"
                                  placeholder="Search or Create Consumable/Services"
                                  defaultOptions={consumableColl}
                                  loadOptions={loadConsumableList}
                                  handleChange={(selected: any) => {
                                    if (!selected) {
                                      return;
                                    }
                                    handleAddConsumableToTable(selected);
                                  }}
                                  value={null}
                                  menuPortalTarget={document.body}
                                  onCreateOption={(inputValue: string) => {
                                    handleClickOpen(
                                      true,
                                      inputValue,
                                      "CREATE_ITEM",
                                      null,
                                    );
                                  }}
                                />
                              </SkeletonWrapper>
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    </>
                  )}
                  {isFieldEnabled("MEDICINE") && !showMedicine && (
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Button
                        variant="text"
                        startIcon={<MedicationOutlinedIcon />}
                        sx={{
                          color: "#285cdb",
                          textTransform: "none",
                        }}
                        onClick={() => setShowMedicine(true)}
                      >
                        MEDICINES
                      </Button>
                    </Grid>
                  )}

                  {isFieldEnabled("MEDICINE") && showMedicine && (
                    <>
                      <Grid item xs={12} sx={{ display: "flex" }}>
                        <Button
                          variant="text"
                          onClick={handleRemoveMedicineSection}
                          sx={{
                            marginLeft: "auto",
                            color: "red",
                            fontSize: "14px",
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                          Remove Medicine
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ width: "100%" }}>
                          <ProcedurePrescription
                            isForProcedure={true}
                            medicineData={singleVisitMedicineData}
                            onMedicineDataChange={handleMedicineDataChange}
                            procedureVisitId={
                              originalProcedure?.procedureVisitColl?.[0]
                                ?.procedureVisitId || null
                            }
                          />
                        </Box>
                      </Grid>
                    </>
                  )}

                  {isFieldEnabled("TEAM_MEMBER") && !showTeamMembers && (
                    <Grid item xs={12} sx={{ mt: 1 }}>
                      <Button
                        variant="text"
                        startIcon={<Groups2OutlinedIcon />}
                        sx={{
                          color: "#285cdb",
                          textTransform: "none",
                        }}
                        onClick={() => setShowTeamMembers(true)}
                      >
                        TEAM MEMBER
                      </Button>
                    </Grid>
                  )}
                  {isFieldEnabled("TEAM_MEMBER") && showTeamMembers && (
                    <>
                      <Grid item xs={12} sx={{ display: "flex" }}>
                        <Button
                          variant="text"
                          onClick={handleRemoveTeamMemberSection}
                          sx={{
                            marginLeft: "auto",
                            color: "red",
                            fontSize: "14px",
                            textTransform: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                          Remove Team Member
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          className="template-table-container"
                          // sx={{ p: 2 }}
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            border: "1px solid #0000001f",
                            borderRadius: 4,
                          }}
                        >
                          <Box style={{ flex: 1, borderRadius: 8 }}>
                            {/* TEAM MEMBERS HEADER */}
                            <Box className="template-box">
                              <Typography>Team Members</Typography>
                            </Box>
                            <Divider />

                            <TeamMembersDraggableList
                              items={teamMemberRows}
                              control={methods.control}
                              loadOptions={loadTeamMembersList}
                              defaultOptions={teamMembersColl}
                              handleCreate={handleTeamCreate}
                              handleSelectChange={handleAddTeamMember}
                              onMoreClick={handleOpenTeamMore}
                            />
                          </Box>
                        </Box>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6} sx={{ mt: 0, mb: 0 }}></Grid>
                  {isFieldEnabled("DURATION") && (
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel className="fileds-label-text">
                          Duration
                        </FormLabel>

                        <SkeletonWrapper
                          isLoading={isLoading}
                          className="skeleton-input"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              border: "1px solid #c4c4c4",
                              borderRadius: "4px",
                              height: "40px",
                              overflow: "hidden",
                            }}
                          >
                            {/* HOURS */}
                            <Controller
                              name="durationHoursValue"
                              control={methods.control}
                              render={({ field }) => (
                                <CustomTextField
                                  {...field}
                                  variant="standard"
                                  type="text"
                                  inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                    maxLength: 4,
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <Box
                                        sx={{
                                          color: "#9e9e9e",
                                          fontSize: "16px",
                                          pointerEvents: "none",
                                          paddingRight: "8px",
                                        }}
                                      >
                                        h
                                      </Box>
                                    ),
                                  }}
                                  sx={{
                                    width: "50%",
                                    "& input": {
                                      textAlign: "right",
                                      paddingRight: "4px",
                                    },
                                    "& .MuiInputBase-root": {
                                      "&:before": { borderBottom: "none" },
                                      "&:after": { borderBottom: "none" },
                                      "&:hover:not(.Mui-disabled):before": {
                                        borderBottom: "none",
                                      },
                                    },
                                  }}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    field.onChange(
                                      val === "" ? "" : Number(val),
                                    );
                                  }}
                                />
                              )}
                            />

                            {/* DIVIDER */}
                            <Box
                              sx={{
                                width: "1px",
                                height: "100%",
                                backgroundColor: "#c4c4c4",
                              }}
                            />

                            {/* MINUTES */}
                            <Controller
                              name="durationMinuteValue"
                              control={methods.control}
                              render={({ field }) => (
                                <CustomTextField
                                  {...field}
                                  variant="standard"
                                  type="text"
                                  inputProps={{
                                    inputMode: "numeric",
                                    pattern: "[0-9]*",
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <Box
                                        sx={{
                                          color: "#9e9e9e",
                                          fontSize: "16px",
                                          pointerEvents: "none",
                                          paddingRight: "8px",
                                        }}
                                      >
                                        m
                                      </Box>
                                    ),
                                  }}
                                  sx={{
                                    width: "50%",
                                    "& input": {
                                      textAlign: "right",
                                      paddingRight: "4px",
                                    },
                                    "& .MuiInputBase-root": {
                                      "&:before": { borderBottom: "none" },
                                      "&:after": { borderBottom: "none" },
                                      "&:hover:not(.Mui-disabled):before": {
                                        borderBottom: "none",
                                      },
                                    },
                                  }}
                                  value={field.value ?? ""}
                                  onChange={(e) => {
                                    let val = e.target.value.replace(/\D/g, "");
                                    if (val === "") {
                                      field.onChange("");
                                      return;
                                    }
                                    const num = Number(val);
                                    field.onChange(num > 59 ? 59 : num);
                                  }}
                                />
                              )}
                            />
                          </Box>
                        </SkeletonWrapper>
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6} />
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box className="procedure-summary-footer">
                  <Box className="procedure-totals-display">
                    <Box className="procedure-total-item">
                      <Typography className="procedure-total-label">
                        Total Price
                      </Typography>
                      <Typography className="procedure-total-value">
                        ₹{" "}
                        {Number(methods.watch("price") || 0).toLocaleString(
                          "en-IN",
                        )}
                      </Typography>
                    </Box>
                    <Box className="procedure-total-item">
                      <Typography className="procedure-total-label">
                        Total Est. Duration
                      </Typography>
                      <Typography className="procedure-total-value">
                        {getNormalizedDurationText()}
                      </Typography>
                    </Box>
                  </Box>

                  <Box className="procedure-actions">
                    <LoadingButton
                      color="primary"
                      loadingPosition="start"
                      startIcon={<></>}
                      variant="outlined"
                      type="button"
                      className="secondary-button procedure-action-button"
                      name="cancel"
                      onClick={handleBack}
                      disabled={isLoading || methods.formState.isSubmitting}
                    >
                      Cancel
                    </LoadingButton>
                    {isForPopUp &&
                      isMemberProcedure &&
                      urlProcedureId === 0 && (
                        <LoadingButton
                          loading={isSaving}
                          color="primary"
                          loadingPosition="start"
                          startIcon={<></>}
                          variant="outlined"
                          type="button"
                          className="secondary-button procedure-action-button"
                          name="save-template"
                          onClick={handleSaveAndTemplate}
                          disabled={isLoading || methods.formState.isSubmitting}
                        >
                          Save
                        </LoadingButton>
                      )}
                    {!isForPopUp && (
                      <LoadingButton
                        loading={isSaving}
                        color="primary"
                        loadingPosition="start"
                        startIcon={<></>}
                        variant="contained"
                        type="submit"
                        className="primary-button procedure-action-button"
                        name="save"
                        disabled={isLoading || methods.formState.isSubmitting}
                      >
                        {urlProcedureId > 0
                          ? "Update Procedure"
                          : "Save Procedure"}
                      </LoadingButton>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        </FormProvider>

        {/* Team Member */}
        <Dialog
          className="dialogPage"
          open={
            customPopUp.open &&
            (customPopUp.code === "CREATE_TEAMMEMBER" ||
              customPopUp.code === "UPDATE_TEAMMEMBER")
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                {customPopUp.code === "CREATE_TEAMMEMBER"
                  ? "Save team member"
                  : "Edit team member"}
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <TeamMemberSave
              handleClosePopUp={(code: string, company: any) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
              code={customPopUp.code}
              teamMemberId={customPopUp?.data?.teamMemberId || 0}
            />
          </DialogContent>
          <Divider />
        </Dialog>

        {/* Item */}
        <Dialog
          className="dialogPage"
          open={
            customPopUp.open &&
            (customPopUp.code === "CREATE_ITEM" ||
              customPopUp.code === "UPDATE_ITEM")
          }
          onClose={(e, reason) => {
            if (reason !== "backdropClick") {
              handleClose();
            }
          }}
          fullScreen
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              width: "1200px",
              height: "700px",
              maxWidth: "none",
            },
          }}
        >
          <AppBar sx={{ position: "relative" }}>
            <Toolbar>
              <Typography
                style={{ fontWeight: 600, marginLeft: 0 }}
                sx={{ ml: 2, flex: 1 }}
                variant="h6"
              >
                {customPopUp.code === "CREATE_ITEM" ? "Save Item" : "Edit Item"}
              </Typography>
            </Toolbar>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </AppBar>
          <DialogContent sx={{ padding: "12px 24px" }}>
            <ItemSave
              handleClosePopUp={(code: string, company: any) => {
                handleManagePopup(code, company);
              }}
              isForPopUp={true}
              name={customPopUp.name}
              code={customPopUp.code}
              itemId={customPopUp?.data?.itemId || 0}
            />
          </DialogContent>
          <Divider />
        </Dialog>

        {/* CONSUMABLE ACTIONS POPOVER (FIXED) */}
        <Popover
          elevation={1}
          open={Boolean(anchorEl3)}
          anchorEl={anchorEl3}
          onClose={handleCloseMore}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          PaperProps={{
            sx: {
              ml: -1, // shifts popover left
              boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
              borderRadius: "10px",
            },
          }}
        >
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleEditConsumable}>
                <ListItemText primary="Edit" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                className="delete-container"
                onClick={handleDeleteConsumable}
              >
                <ListItemText primary="Delete" />
              </ListItemButton>
            </ListItem>
          </List>
        </Popover>

        {/* TEAM MEMBER ACTIONS POPOVER (FIXED) */}
        <Popover
          elevation={1}
          open={Boolean(teamAnchorEl)}
          anchorEl={teamAnchorEl}
          onClose={handleCloseTeamMore}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          PaperProps={{
            sx: {
              ml: -1,
              boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
              borderRadius: "10px",
            },
          }}
        >
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleEditTeamMember}>
                <ListItemText primary="Edit" />
              </ListItemButton>
            </ListItem>

            <ListItem disablePadding>
              <ListItemButton
                className="delete-container"
                onClick={handleDeleteTeamMember}
              >
                <ListItemText primary="Delete" />
              </ListItemButton>
            </ListItem>
          </List>
        </Popover>
      </Box>
    </Box>
  );
};
export default SubProcedureVisit;
